import axios from 'axios';
import {
    deleteMaintenanceDraft,
    deleteMaintenanceOutbox,
    deleteMaintenancePhoto,
    getMaintenancePhoto,
    putMaintenanceOutbox,
    putMaintenancePhoto,
} from './maintenanceDb';
import { uploadTemporaryFile } from './uploadTemporaryFile';

export const MAINTENANCE_OUTBOX_STATES = Object.freeze({
    pending: 'pending',
    uploading: 'uploading',
    submitting: 'submitting',
    validationError: 'validation-error',
    conflict: 'conflict',
    failed: 'failed',
    synced: 'synced',
});

const uploadPhoto = async (scope, photoId, client) => {
    let photo = await getMaintenancePhoto(scope, photoId);
    if (!photo) {
        throw new Error(`Foto locale non trovata: ${photoId}`);
    }

    if (!photo.tempFile?.tmp) {
        if (!photo.blob) {
            throw new Error(`File della foto locale non trovato: ${photoId}`);
        }
        photo = await putMaintenancePhoto(scope, {
            ...photo,
            tempFile: await uploadTemporaryFile(
                photo.blob,
                photo.originalName || `${photo.id}.jpg`,
                client,
            ),
        });
    }

    if (photo.drawingBlob && !photo.drawingTempFile?.tmp) {
        photo = await putMaintenancePhoto(scope, {
            ...photo,
            drawingTempFile: await uploadTemporaryFile(
                photo.drawingBlob,
                photo.drawingOriginalName || `${photo.id}-drawing.png`,
                client,
            ),
        });
    }

    if (photo.state !== 'uploaded-temp') {
        photo = await putMaintenancePhoto(scope, { ...photo, state: 'uploaded-temp' });
    }

    return photo;
};

const uploadInChunks = async (scope, photoIds, client, concurrency = 2) => {
    const uploaded = [];
    for (let index = 0; index < photoIds.length; index += concurrency) {
        const chunk = photoIds.slice(index, index + concurrency);
        const results = await Promise.all(chunk.map(id => uploadPhoto(scope, id, client)));
        uploaded.push(...results);
    }
    return uploaded;
};

const localPhotoId = value => value && typeof value === 'object'
    ? (value.localPhotoId ?? null)
    : null;

export const maintenancePhotoIdsFromPayload = payload => [
    ...new Set((Array.isArray(payload?.photos) ? payload.photos : [])
        .map(row => localPhotoId(row?.file))
        .filter(Boolean)),
];

const photoPayload = photo => ({
    file: photo.tempFile,
    drawing: photo.drawingBlob ? photo.drawingTempFile : null,
    photoTypeId: photo.photoTypeId ?? null,
    caption: photo.caption ?? null,
    capturedAt: photo.capturedAt ?? null,
    latitude: photo.latitude ?? null,
    longitude: photo.longitude ?? null,
    locationAccuracy: photo.locationAccuracy ?? null,
});

const hydratePhotoRows = (rows, photos) => {
    const photosById = new Map(photos.map(photo => [String(photo.id), photo]));

    return rows.map((row) => {
        const id = localPhotoId(row?.file);
        if (!id) return row;

        const photo = photosById.get(String(id));
        if (!photo) throw new Error(`Foto locale non trovata: ${id}`);

        const drawingId = localPhotoId(row?.drawing);
        if (drawingId && !photo.drawingTempFile?.tmp) {
            throw new Error(`Disegno locale non trovato: ${drawingId}`);
        }

        return {
            ...row,
            file: photo.tempFile,
            drawing: drawingId ? photo.drawingTempFile : (row?.drawing ?? null),
        };
    });
};

const errorState = error => {
    const status = error?.response?.status ?? null;
    if (status === 422) return MAINTENANCE_OUTBOX_STATES.validationError;
    if ([409, 423].includes(status)) return MAINTENANCE_OUTBOX_STATES.conflict;
    return MAINTENANCE_OUTBOX_STATES.failed;
};

export async function syncMaintenanceOutboxEntry(scope, entry, options = {}) {
    const client = options.client ?? axios;
    const uploadConcurrency = options.uploadConcurrency ?? 2;
    let current = await putMaintenanceOutbox(scope, {
        ...entry,
        state: MAINTENANCE_OUTBOX_STATES.uploading,
        lastError: null,
    });

    try {
        const photos = await uploadInChunks(
            scope,
            Array.isArray(current.photoIds) ? current.photoIds : [],
            client,
            uploadConcurrency,
        );
        const payload = {
            ...(current.payload ?? {}),
            clientUuid: current.clientUuid,
        };
        if (photos.length) {
            const relation = current.photoRelation ?? 'photos';
            payload[relation] = Array.isArray(payload[relation])
                ? hydratePhotoRows(payload[relation], photos)
                : photos.map(photoPayload);
        }

        current = await putMaintenanceOutbox(scope, {
            ...current,
            state: MAINTENANCE_OUTBOX_STATES.submitting,
        });

        const response = await client.post(current.endpoint, payload, {
            headers: {
                ...(current.headers ?? {}),
                'X-Idempotency-Key': current.idempotencyKey,
            },
        });

        await putMaintenanceOutbox(scope, {
            ...current,
            state: MAINTENANCE_OUTBOX_STATES.synced,
            responseData: response.data,
        });
        await deleteMaintenanceOutbox(scope, current.id);
        await deleteMaintenanceDraft(scope, current.draftKey, { deletePhotos: true });

        return { response, state: MAINTENANCE_OUTBOX_STATES.synced };
    } catch (error) {
        const state = errorState(error);
        await putMaintenanceOutbox(scope, {
            ...current,
            state,
            retryCount: (current.retryCount ?? 0) + 1,
            lastError: {
                status: error?.response?.status ?? null,
                message: error?.response?.data?.message ?? error?.message ?? 'Errore di sincronizzazione',
                errors: error?.response?.data?.errors ?? null,
                occurredAt: new Date().toISOString(),
            },
        });
        throw error;
    }
}

export function shouldRetryMaintenanceSync(entry) {
    return [
        MAINTENANCE_OUTBOX_STATES.pending,
        MAINTENANCE_OUTBOX_STATES.failed,
    ].includes(entry?.state);
}